import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { billingAPI } from "../../lib/api";
import { formatAUD, frequencyLabel } from "../../lib/billing";
import { CalendarClock, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-AU", { month: "long", year: "numeric" });
};

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export function MonthlyRenewals({ refreshKey, onSelectCustomer }) {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await billingAPI.getRenewals(month);
      setData(res.data);
    } catch {
      setData(null);
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!data) return null;

  return (
    <Card className="border-l-4 border-l-cyan-500" data-testid="monthly-renewals">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-cyan-600" />
              Renewals — {monthLabel(data.month)}
            </CardTitle>
            <CardDescription>Services renewing this month, by client</CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="renewals-prev-month">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {month !== currentMonth() && (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setMonth(currentMonth())} data-testid="renewals-current-month">
                Today
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="renewals-next-month">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.clients.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">No renewals in {monthLabel(data.month)}.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.clients.map((client) => (
              <div
                key={client.customer_id}
                className="border border-slate-200 rounded-lg p-3 hover:border-cyan-300 cursor-pointer transition-colors"
                onClick={() => onSelectCustomer?.(client.customer_id)}
                data-testid={`renewals-client-${client.customer_id}`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <p className="font-semibold text-slate-800 text-sm">{client.customer_name}</p>
                  <p className="text-sm font-bold text-cyan-700">{formatAUD(client.total)}</p>
                </div>
                <div className="space-y-1">
                  {client.services.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-slate-600">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{s.service_name}</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">{frequencyLabel(s.frequency)}</Badge>
                        {s.renewal_day && <span className="text-slate-400 shrink-0">on the {ordinal(s.renewal_day)}</span>}
                      </span>
                      <span className="font-medium shrink-0 ml-2">{formatAUD(s.price)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t text-sm" data-testid="renewals-grand-total">
          <span className="text-slate-600">
            Total <b>{data.total_services}</b> service{data.total_services !== 1 ? "s" : ""} renewing
          </span>
          <span className="font-bold text-slate-900">Total renewal charge: {formatAUD(data.total_charge)}</span>
        </div>

        {data.missing_dates.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="renewals-missing-dates">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              {data.missing_dates.length} non-monthly service{data.missing_dates.length !== 1 ? "s have" : " has"} no renewal date set and can't be shown here:{" "}
              {data.missing_dates.slice(0, 5).map((m) => `${m.customer_name} — ${m.service_name}`).join("; ")}
              {data.missing_dates.length > 5 && ` and ${data.missing_dates.length - 5} more`}.
              {" "}Edit each profile and set the "Next Renewal Date".
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
